/**
 * PDF books page component.
 *
 * Displays available PDF learning resources organized by proficiency level.
 * Provides search functionality, level filtering, and download/lock status
 * indicators for each book.
 *
 * Route: `/learning/books`
 * Requires authentication: Yes
 *
 * @module PDFBooks
 */

import { useState } from "react";
import { Download, Lock, Eye, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LearningSidebar } from "@/components/learning/LearningSidebar";
import { SearchBar } from "@/components/learning/SearchBar";
import { pdfBooks } from "@/data/learningData";
import { ProficiencyLevel } from "@/types/learning";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export default function PDFBooks() {
  const { t } = useTranslation();
  const [selectedLevels, setSelectedLevels] = useState<ProficiencyLevel[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const filteredBooks = pdfBooks.filter((book) => {
    if (
      selectedLevels.length > 0 &&
      book.level &&
      !selectedLevels.includes(book.level)
    ) {
      return false;
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return book.title.toLowerCase().includes(query);
    }
    return true;
  });

  const sortedBooks = [...filteredBooks].sort((a, b) => {
    return a.title.localeCompare(b.title);
  });

  const handleLevelToggle = (level: ProficiencyLevel) => {
    setSelectedLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level],
    );
  };

  const handleClearLevels = () => {
    setSelectedLevels([]);
  };

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-background lg:flex-row">
      {/* Sidebar */}
      <LearningSidebar
        activeContentType="books"
        selectedLevels={selectedLevels}
        onLevelToggle={handleLevelToggle}
        onClearLevels={handleClearLevels}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto relative">
        <div className="mx-auto max-w-[1200px] p-3 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6">
            <div className="mb-4 flex items-center justify-end">
              <div className="flex items-center gap-2">
                <LanguageSwitcher />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsSidebarOpen(true)}
                  className="flex items-center gap-2 lg:hidden"
                >
                  <Menu className="h-4 w-4" />
                  <span>
                    {t("learning.sidebar.filters", { defaultValue: "Filters" })}
                  </span>
                </Button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
              <h1 className="font-ui text-2xl sm:text-3xl font-semibold text-foreground">
                {t("learning.books.title", { defaultValue: "PDF Books" })}
              </h1>
            </div>
            <p className="font-ui text-sm text-muted-foreground mb-6">
              {t("learning.books.subtitle", {
                defaultValue:
                  "Access downloadable PDF resources for your learning journey",
              })}
            </p>
            <div className="flex items-center gap-4">
              <SearchBar
                placeholder={t("learning.books.searchPlaceholder", {
                  defaultValue: "Search books…",
                })}
                onSearch={setSearchQuery}
              />
            </div>
          </div>

          {/* Books List */}
          <div className="space-y-2">
            {sortedBooks.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <p className="font-ui">
                  {t("learning.books.noBooks", {
                    defaultValue: "No books found",
                  })}
                </p>
                {(selectedLevels.length > 0 || searchQuery) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => {
                      setSelectedLevels([]);
                      setSearchQuery("");
                    }}
                  >
                    {t("learning.clearFilters", {
                      defaultValue: "Clear filters",
                    })}
                  </Button>
                )}
              </div>
            ) : (
              sortedBooks.map((book) => (
                <div
                  key={book.id}
                  className={cn(
                    "flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-card border-2 border-border rounded-lg gap-4",
                    book.isLocked && "opacity-60",
                  )}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-ui font-semibold text-base text-foreground">
                        {book.title}
                      </h3>
                      {book.level && (
                        <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary font-ui text-xs">
                          {book.level}
                        </span>
                      )}
                      {book.isLocked && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs font-ui">
                          <Lock className="h-3 w-3" />
                          {t("common.locked", { defaultValue: "Locked" })}
                        </span>
                      )}
                    </div>
                    {book.fileSize && (
                      <p className="font-ui text-sm text-muted-foreground">
                        {book.fileSize}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto pt-3 sm:pt-0 border-t sm:border-t-0 border-border">
                    {book.isLocked ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled
                          className="flex-1 sm:flex-none"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          {t("common.view", { defaultValue: "View" })}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled
                          className="flex-1 sm:flex-none"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          {t("common.download", { defaultValue: "Download" })}
                        </Button>
                      </>
                    ) : (
                      <>
                        {book.downloadUrl && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 sm:flex-none"
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              {t("common.view", { defaultValue: "View" })}
                            </Button>
                            <Button size="sm" className="flex-1 sm:flex-none">
                              <Download className="h-4 w-4 mr-2" />
                              {t("common.download", {
                                defaultValue: "Download",
                              })}
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
